// Studio-mode signal + helpers. Studio operates on a single dedicated Dotgrad
// layer. Enter ensures one exists; exit just toggles UI back. The layer
// persists between toggles and is fully editable in Pro mode.

import { signal, type ReadSignal } from '@/core/signals';
import { state, addLayer, selectLayer, setResolution } from '@/core/state';
import { makeDefaultLayer } from '@/core/pipeline';
import type { Layer, Resolution } from '@/core/types';

const _isStudio = signal<boolean>(false);

export const isStudio: ReadSignal<boolean> = {
  get: _isStudio.get.bind(_isStudio),
  peek: _isStudio.peek.bind(_isStudio),
};

/** Find the existing Dotgrad layer (selected one preferred), else null. */
export function findDotgradLayer(): Layer | null {
  const s = state.peek();
  // Prefer selected, if it is a dotgrad
  if (s.selectedLayerId) {
    const sel = s.layers.find((l) => l.id === s.selectedLayerId);
    if (sel && sel.kind === 'dotgrad') return sel;
  }
  return s.layers.find((l) => l.kind === 'dotgrad') ?? null;
}

/** Returns the existing Dotgrad layer or creates one and selects it. */
export function ensureDotgradLayer(): Layer {
  const existing = findDotgradLayer();
  if (existing) {
    selectLayer(existing.id);
    return existing;
  }
  const tmpl = makeDefaultLayer('dotgrad', '_');
  const id = addLayer({
    kind: tmpl.kind,
    params: tmpl.params,
    blend: tmpl.blend,
    opacity: tmpl.opacity,
    enabled: tmpl.enabled,
    seed: tmpl.seed,
  });
  // addLayer selects the new layer; fetch fresh.
  const created = state.peek().layers.find((l) => l.id === id);
  if (!created) throw new Error('studio: could not create dotgrad layer');
  return created;
}

function requestFit(): void {
  if (typeof window !== 'undefined') {
    // Defer until layout has settled after class swap.
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('pixellab:fit'));
    });
  }
}

let savedResolution: Resolution | null = null;

/**
 * Pick a source resolution whose aspect matches the current window so the
 * canvas can be CSS-stretched to fill 100×100% with crisp pixelated rendering
 * and no rectangle distortion of the dots.
 */
function studioResolution(): Resolution {
  const winW = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
  // Target ~80 dots across the long side (matches the reference dot density).
  // Default cell=8 means source long-side ≈ 80 * 8 = 640 px.
  const longSide = 640;
  const aspect = winW / winH;
  let w: number, h: number;
  if (aspect >= 1) {
    w = longSide;
    h = Math.max(160, Math.round(longSide / aspect / 8) * 8);
  } else {
    h = longSide;
    w = Math.max(160, Math.round(longSide * aspect / 8) * 8);
  }
  return { w, h };
}

export function enterStudio(): void {
  if (_isStudio.peek()) return;
  ensureDotgradLayer();
  // Save current resolution and switch to a window-matched one for full-bleed.
  savedResolution = { ...state.peek().resolution };
  setResolution(studioResolution());
  _isStudio.set(true);
  if (typeof document !== 'undefined') {
    document.body.classList.add('studio');
  }
  requestFit();
}

export function exitStudio(): void {
  if (!_isStudio.peek()) return;
  _isStudio.set(false);
  if (typeof document !== 'undefined') {
    document.body.classList.remove('studio');
  }
  // Restore the user's pre-Studio resolution (if it differed from what we set).
  if (savedResolution) {
    setResolution(savedResolution);
    savedResolution = null;
  }
  requestFit();
}

export function toggleStudio(): void {
  if (_isStudio.peek()) exitStudio();
  else enterStudio();
}
